// Detect GitNexus CLI ("gitnexus" binary) trên PATH + parse SemVer version.
// Trả structured info để các module sau (install / setup / analyze) decide flow.
// Cross-platform: Unix dùng `which`, Windows dùng `where`.
//
// Pattern identical với detect-claude-code-installation.ts — reuse design.
import { spawnSync } from "node:child_process";
import { detectHostPlatform } from "./detect-host-platform.js";

export interface GitnexusInstallationInfo {
  installed: boolean;
  version: string | null; // SemVer string khi detect được, vd "1.6.5"
  path: string | null; // Absolute path đến binary nếu tìm thấy
}

// Timeout cho `gitnexus --version` — nếu binary hang thì abort tránh block init.
const VERSION_PROBE_TIMEOUT_MS = 5000;

// Regex SemVer chấp nhận extra suffix vd "1.6.5 (GitNexus)" hoặc "gitnexus 1.6.5".
const SEMVER_REGEX = /(\d+\.\d+\.\d+)/;

// Probe binary location qua `which` (POSIX) hoặc `where` (Windows).
// Cả 2 đều là external executable → KHÔNG cần shell:true → tránh DEP0190 Node 23+.
function probeGitnexusBinaryPath(): string | null {
  const isWindows = detectHostPlatform() === "win32";
  const probeCmd = isWindows ? "where" : "which";
  const result = spawnSync(probeCmd, ["gitnexus"], { encoding: "utf8" });

  if (result.error || result.status !== 0) return null;
  const out = (result.stdout || "").trim();
  if (!out) return null;
  // `where` có thể list nhiều dòng — lấy dòng đầu.
  // out đã verified non-empty (line 30) → split[0] luôn tồn tại.
  return out.split(/\r?\n/)[0]!.trim();
}

// Parse `gitnexus --version` output → SemVer. Null nếu không match.
function probeGitnexusVersion(): string | null {
  const result = spawnSync("gitnexus", ["--version"], {
    encoding: "utf8",
    timeout: VERSION_PROBE_TIMEOUT_MS,
  });

  if (result.error || result.status !== 0) return null;
  const out = (result.stdout || "").trim();
  const match = SEMVER_REGEX.exec(out);
  return match?.[1] ?? null;
}

// Main API: detect GitNexus installation state.
// Không throw — luôn return structured info để caller branch logic.
//
// v1.13.0 perf: Module-level cache. Init flow gọi 2-3 lần → save 500ms-1.5s.
// Caller invalidate qua `invalidateGitnexusInstallationCache()` sau install.
let cachedInfo: GitnexusInstallationInfo | null = null;

export function detectGitnexusInstallation(): GitnexusInstallationInfo {
  if (cachedInfo !== null) return cachedInfo;
  const path = probeGitnexusBinaryPath();
  if (!path) {
    cachedInfo = { installed: false, version: null, path: null };
    return cachedInfo;
  }
  const version = probeGitnexusVersion();
  cachedInfo = { installed: true, version, path };
  return cachedInfo;
}

export function invalidateGitnexusInstallationCache(): void {
  cachedInfo = null;
}
